import { AsyncPipe, NgClass, NgOptimizedImage } from '@angular/common';
import { Component, Inject, inject, input, Input, LOCALE_ID, OnInit, output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { StoreSpecificConfigService } from '@scalefast/config-angular';
import { BuilderService, SubscribeNewsletterModalService } from '@scalefast/ecommerce-core';
import { asapScheduler, Observable } from 'rxjs';
import { MISSING_PRODUCT_IMAGE_URL } from 'src/app/app.tokens';
import { LocaleDataPipe } from '../../pipes/locale-data.pipe';
import { AnalyticsStoreService } from '../../services';
import { NewsletterService } from '../../services/newsletter.service';
import { BuilderContent } from '@builder.io/sdk';

type SubscribeNewsletterForm = {
  email: string;
  subscribe_newsletter: boolean;
  subscribe_promotion: boolean;
  extra?: Record<string, [{ value: string }]>;
};

type SubscribeNewsletterInput = {
  name: string;
  formControl: FormControl;
};

interface C360Selection {
  image: string;
  text: string;
  value: string;
}

@Component({
  selector: 'app-subscribe-newsletter-component',
  styleUrls: ['./subscribe-newsletter.component.scss', './subscribe-newsletter-extra.scss', './subscribe-newsletter-popup.scss'],
  templateUrl: './subscribe-newsletter.component.html',
  standalone: true,
  imports: [NgClass, RouterLink, NgOptimizedImage, ReactiveFormsModule, AsyncPipe, LocaleDataPipe],
  // TODO:  changeDetection: ChangeDetectionStrategy.OnPush,
  // Applying onPush changeDetectionStrategy to this component, the SubscribeNewsletterModalComponent is loaded before the content,
  // causing a weird behaviour. Needs more investigation.
})
export class AppSubscribeNewsletterUIComponent implements OnInit {
  emailPlaceholder = input('Email');
  submitLabel = input('Subscribe');
  sectionTitle = input('Subscribe to our newsletter');
  checkboxNewsletterText = input('I have read and agree with the');
  checkboxPromotionText = input('I want promotions');
  linkText = input('Privacy Policy.');
  namePlaceholder = input('Full Name');
  hasNewsletter = input<boolean | undefined | null>(true);
  subscribedToNewsletter = input<boolean | undefined>(false);
  newsletterFormEvent = output<SubscribeNewsletterForm>();
  newsletterForm: FormGroup;
  isSubmitted = false;
  isPromotionChecked = false;
  isNewsletterChecked = false;
  extraInputs: SubscribeNewsletterInput[] = [];

  showSubmitLabel = input<boolean>(true);
  newsletterFooter = input<boolean>(true);
  // TODO: this input could be probably changed to a toSignal()
  @Input() newsletterData: any = undefined;

  showNewsletter$: Observable<boolean>;
  localization: string = '';
  hiddenLabelsControl: number = 0;

  subscribed: boolean = false;
  hideAfterSubmit: boolean = false;
  showNewsletterFooterBeforeSubscrited: boolean = false;
  readonly C360inputName: string = 'selection';
  C360SelectionsEnabled: boolean = false;
  C360SelectionsTitle: string = '';
  C360Selections: C360Selection[] = [];
  currentc360Selection: C360Selection | undefined;
  subscribeNewsletterModalService = inject(SubscribeNewsletterModalService);

  constructor(
    protected formBuilder: FormBuilder,
    private builderService: BuilderService,
    private analyticsStoreService: AnalyticsStoreService,
    private newsletterService: NewsletterService,
    @Inject(MISSING_PRODUCT_IMAGE_URL) public missingProductImageUrl: string,
    @Inject(LOCALE_ID) private locale: string,
    private storeConfigService: StoreSpecificConfigService,
  ) {
    this.newsletterForm = new FormGroup('');
    this.extraInputs = [{ name: 'name', formControl: new FormControl('') }];
    this.localization = locale;
    this.builderService.getContentByModelName('newsletter').subscribe((content: BuilderContent) => {
      if (!content.data) {
        return;
      }
      this.newsletterData = content.data;
    });
    this.showNewsletter$ = this.newsletterService.showNewsletter$;
    this.showNewsletterFooterBeforeSubscrited = this.subscribedToNewsletter() ? true : false;
    const storeConfig = this.storeConfigService.get();
    this.C360SelectionsEnabled = storeConfig?.integrations?.c360?.newsletter_selection?.enabled ?? false;
    this.C360SelectionsTitle = storeConfig?.integrations?.c360?.newsletter_selection?.title ?? false;
    this.C360Selections = storeConfig?.integrations?.c360?.newsletter_selection?.selections ?? [];
    if (this.C360SelectionsEnabled) {
      this.extraInputs.push({ name: this.C360inputName, formControl: new FormControl('') });
    }
  }

  ngOnInit() {
    this.newsletterForm = this.formBuilder.group({
      email: new FormControl('', [
        Validators.required,
        Validators.email,
        Validators.pattern('^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$'),
      ]),
      subscribe_newsletter: new FormControl(false, [Validators.required]),
      subscribe_promotion: new FormControl(false),
    });

    this.extraInputs.map((input: SubscribeNewsletterInput) => {
      this.newsletterForm.addControl(input.name, input.formControl);
    });
  }

  get formControls() {
    return this.newsletterForm.controls;
  }

  subscribeNewsletter() {
    this.isSubmitted = true;
    if (this.newsletterForm.invalid) {
      return;
    }
    const subscribeNewsletterForm: SubscribeNewsletterForm = {
      email: this.newsletterForm.controls.email.value,
      subscribe_newsletter: this.newsletterForm.controls.subscribe_newsletter.value,
      subscribe_promotion: this.newsletterForm.controls.subscribe_promotion.value,
    };

    if (this.extraInputs.length > 0) {
      const extra: Record<string, [{ value: string }]> = {};
      this.extraInputs.map((input) => {
        const value = input.formControl.value;
        const key = input.name;
        extra[key] = [{ value: value }];
      });
      subscribeNewsletterForm.extra = extra;
    }

    this.newsletterFormEvent.emit(subscribeNewsletterForm);
    // This asapScheduler is here in order to clear the subscribe form after the user has been subscribed
    asapScheduler.schedule(() => {
      this.clearNewsletterForm();
    }, 3000);
    this.closeNewsletterModal();
  }

  onPrivacyPolicyCheckChange() {
    this.isNewsletterChecked = !this.isNewsletterChecked;
  }

  onPromotionCheckChange() {
    this.isPromotionChecked = !this.isPromotionChecked;
  }

  clearNewsletterForm() {
    this.newsletterForm.reset();
    this.newsletterForm.markAsPristine();
    Object.keys(this.newsletterForm.controls).forEach((key) => {
      this.newsletterForm.get(key)?.setErrors(null);
    });
    this.newsletterForm.controls.email.setValue('');
    this.extraInputs.map((input) => {
      this.newsletterForm.get(input.name)?.setValue('');
    });
    this.isSubmitted = false;
    this.isNewsletterChecked = false;
    this.isPromotionChecked = false;
  }

  selectC360Element(c360Selection: C360Selection) {
    this.currentc360Selection = c360Selection;
    this.newsletterForm.controls[this.C360inputName].setValue(c360Selection.value ?? '');
  }

  gtmEvent(event: any): void {
    const category = event.currentTarget.getAttribute('data-gtmcategory');
    const action = event.currentTarget.getAttribute('data-gtmaction');
    const label = event.currentTarget.getAttribute('data-gtmlabel');
    this.analyticsStoreService.miscEvent(category, action, label);
  }

  generateLocalizedURL(id: number): string {
    const baseUrls: { [key: string]: { [key: number]: string } } = {
      'fr-FR': {
        1: 'https://preferencecenter.pg.com/fr-fr/datarequests/',
        2: 'https://privacypolicy.pg.com/fr-FR/',
        3: 'https://termsandconditions.pg.com/fr-fr/',
        4: 'https://preferencecenter.pg.com/fr-fr/consent-to-advertising/',
      },
      'es-ES': {
        1: 'https://preferencecenter.pg.com/es-es/datarequests/',
        2: 'https://privacypolicy.pg.com/es-ES/',
        3: 'https://termsandconditions.pg.com/es-es/',
        4: 'https://preferencecenter.pg.com/es-es/consent-to-advertising/',
      },
    };

    const validLocales = ['fr-FR', 'es-ES'];

    if (validLocales.includes(this.localization)) {
      const baseUrl = baseUrls[this.localization] as { [key: number]: string } | undefined;
      return baseUrl ? baseUrl[id] || '#' : '#';
    }

    return '#';
  }

  showHiddenLabels(id: number) {
    this.hiddenLabelsControl = this.hiddenLabelsControl === id ? 0 : id;
  }

  checkInputDefault() {
    this.newsletterForm.get('subscribe_newsletter')?.setValue(true);
    if (this.localization === 'fr-FR') {
      this.newsletterForm.get('subscribe_promotion')?.setValue(true);
    }
  }

  closeNewsletterModal() {
    this.subscribed = true;
    setTimeout(() => {
      this.subscribeNewsletterModalService.hideModalAndSetLocalStorage();
    }, 10000);
  }
}
